import React from 'react';
import { useTheme } from '@veneer/theme';
import SelectInput from './select_input';
import propTypes, { deprecatedSelectPlacements } from './prop_types';
import {
  DeprecatedSelectPlacement,
  SelectInterface,
  SelectOption,
  SelectValue,
} from './types';
import selectI18n from './i18n';
import HelperText from '../shared/form/helper_text';
import useInputId from '../shared/form/use_input_id';
import Label from '../shared/form/label';
import useInputStyles from '../shared/form/use_input_styles';
import {
  DEFAULT_MIN_WIDTH,
  POPOVER_WIDTH_AUTO,
  NODE_ENV_DEV,
} from './defaults';

function getNewValue(
  value: SelectValue[],
  newValue: SelectOption,
  multiple = false
) {
  let valueArray = [];

  if (multiple) {
    const index = value.indexOf(newValue.value);

    if (index !== -1) {
      valueArray = value.filter((v, i) => i !== index);
    } else {
      valueArray = [...value, newValue.value];
    }
  } else {
    valueArray = [newValue.value];
  }

  return valueArray;
}

let placementWarned = false;
let wrapWarned = false;
let wrapNoWrapWarned = false;

const Select = React.forwardRef<
  HTMLInputElement,
  SelectInterface &
    Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof SelectInterface>
>(
  (
    {
      className,
      clearIcon = true,
      defaultValue: defaultValueProp = [],
      disabled = false,
      error = false,
      helperText,
      helperTextState,
      helperTextVisibility = 'auto',
      i18n: i18nProp,
      id: idProp,
      label,
      disableLabelShrinking = false,
      multiple = false,
      name,
      onChange,
      onClear,
      onScrollEnd,
      onSearch,
      options,
      placeholder,
      placement,
      portalContainer,
      preselectedOptions = [],
      readOnly = false,
      required = false,
      scrollEndOffset = 0,
      searchDelay,
      separateLabel = false,
      showSearch = false,
      value: valueProp,
      visibleOptions = 10,
      noTruncate = false,
      wrap,
      popoverWidth = POPOVER_WIDTH_AUTO,
      noWrap = false,
      ...other
    },
    ref
  ) => {
    if (
      process.env.NODE_ENV === NODE_ENV_DEV &&
      !placementWarned &&
      deprecatedSelectPlacements.includes(
        placement as DeprecatedSelectPlacement
      )
    ) {
      console.warn(
        [
          "@veneer/core: The placements: [bottom-start, bottom-end, top-start, top-end] have been deprecated since v3.115.0 and it'll be removed in a future release.",
          'Please use bottom or top placements since popover width is equal to input width.',
        ].join(' ')
      );
      placementWarned = true;
    }

    if (
      process.env.NODE_ENV === NODE_ENV_DEV &&
      !wrapWarned &&
      deprecatedSelectPlacements.includes(
        placement as DeprecatedSelectPlacement
      )
    ) {
      console.warn(
        [
          "@veneer/core: The `wrap` property has been deprecated since v3.118.0 and it'll be removed in a future release.",
          'Default behavior has changed to Wrap items, so remove it if thats the expected behavior. It expected behavior is to Truncate items with ellipsis, then use the `noWrap` property.',
        ].join(' ')
      );
      wrapWarned = true;
    }

    if (
      process.env.NODE_ENV === NODE_ENV_DEV &&
      !wrapNoWrapWarned &&
      deprecatedSelectPlacements.includes(
        placement as DeprecatedSelectPlacement
      )
    ) {
      console.warn(
        [
          '@veneer/core: Please do not use `wrap` and `noWrap`properties at the same time! Use only `noWrap`!',
        ].join(' ')
      );
      wrapNoWrapWarned = true;
    }

    const theme = useTheme();
    const isHd = theme.density === 'high';

    if (wrap !== undefined) {
      noWrap = !wrap;
    }

    if (typeof popoverWidth === 'number' && popoverWidth < DEFAULT_MIN_WIDTH) {
      popoverWidth = DEFAULT_MIN_WIDTH;
    }

    const { id, helperTextId, labelId } = useInputId('vn-select', idProp);
    const containerStyle = useInputStyles({});

    const i18n = React.useMemo(
      () => ({
        ...selectI18n,
        ...i18nProp,
      }),
      [i18nProp]
    );

    const { current: isControlled } = React.useRef(valueProp != null);
    const [defaultValue, setDefaultValue] = React.useState(defaultValueProp);
    const [open, setOpen] = React.useState(false);
    const value = (isControlled ? valueProp : defaultValue) as SelectValue[];

    const handleChange = React.useCallback(
      (newValue: SelectOption) => {
        if (!isControlled) {
          setDefaultValue((prevValue) =>
            getNewValue(prevValue, newValue, multiple)
          );
        }

        onChange?.(newValue);
      },
      [isControlled, multiple, onChange]
    );

    const handleUpdateOpen = React.useCallback((newValue: boolean) => {
      setOpen(newValue);
    }, []);

    const handleClear = React.useCallback(() => {
      if (!isControlled) {
        setDefaultValue([]);
      }

      onClear?.();
    }, [isControlled, onClear]);

    const handleLabelMouseDown = React.useCallback(() => {
      setOpen(true);
    }, []);

    return (
      <div
        className={className}
        css={[
          containerStyle.root.base,
          helperTextVisibility === 'auto' &&
            containerStyle.root.helperTextVisible,
          { width: '100%' },
        ]}
      >
        {label && (separateLabel || isHd) && (
          <Label
            disabled={disabled}
            id={labelId}
            required={required ?? false}
            truncate={!noTruncate}
          >
            {label}
          </Label>
        )}

        <SelectInput
          error={error}
          disabled={disabled}
          label={label}
          separateLabel={separateLabel}
          disableLabelShrinking={disableLabelShrinking}
          required={required}
          handleLabelMouseDown={handleLabelMouseDown}
          helperTextId={helperTextId}
          clearIcon={clearIcon}
          i18n={i18n}
          id={id}
          labelId={labelId}
          multiple={multiple}
          name={name}
          onChange={handleChange}
          onClear={handleClear}
          onScrollEnd={onScrollEnd}
          onSearch={onSearch}
          onUpdateOpen={handleUpdateOpen}
          open={open}
          options={options}
          placeholder={placeholder}
          placement={placement}
          portalContainer={portalContainer}
          preselectedOptions={preselectedOptions}
          readOnly={readOnly}
          ref={ref}
          scrollEndOffset={scrollEndOffset}
          searchDelay={searchDelay}
          showSearch={showSearch}
          value={value}
          visibleOptions={visibleOptions}
          noWrap={noWrap}
          popoverWidth={popoverWidth}
          {...other}
        />
        {helperText && (
          <HelperText
            disabled={disabled}
            error={error}
            helperTextState={helperTextState}
            helperTextVisibility={helperTextVisibility}
            id={helperTextId}
          >
            {helperText}
          </HelperText>
        )}
      </div>
    );
  }
);

Select.propTypes = propTypes;

Select.displayName = 'Select';

export default Select as <T extends SelectValue>(
  props: React.PropsWithoutRef<SelectInterface<T>> &
    Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof SelectInterface> &
    React.RefAttributes<HTMLInputElement>
) => React.ReactElement | null;
